import { LevelIcon, PrivacyLockIcon, RadioButtonThick } from '@va/icons';
import { useTranslate } from '@va/localization';
import { contributorRole } from '@va/standalone/shared/selectors';
import { PrivacyLevelsEnum } from '@va/standalone/shared/types';
import { DisabledOverlay, Paragraph, fontWeights, paragraphSizes } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { useFocusedActions } from '@va/util/hooks';
import classNames from 'classnames';
import { PropsWithChildren, ReactNode, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';

type PrivacyLevelCardWrapperProps = {
  onClick: (value: PrivacyLevelsEnum) => void;
  selected: boolean;
  value: PrivacyLevelsEnum;
  baseKey: string;
  className?: string;
  onHover?: () => void;
  variant?: 'default' | 'wix-mini';
  disabled?: boolean;
  disabledTooltip?: ReactNode;
};

const styles = {
  default: {
    card: {
      base: 'bg-white-snow hover:bg-gray-concrete p-3 rounded-30',
      selected: 'bg-gray-concrete',
    },
    iconContainer: {
      base: 'bg-white',
      selected: 'bg-blue-pure',
    },
    levelIconColor: {
      base: '#2478F0',
      selected: '#FFFFFF',
      inactive: '#F0F0F0',
      selectedInactive: '#6DA8FC',
    },
    iconColor: {
      base: '#969696',
      selected: '#FFFFFF',
    },
  },
  'wix-mini': {
    card: {
      base: 'bg-[#000624]/[0.05] p-1.5 rounded-[1.5rem]',
      selected: '!bg-white hover:bg-white',
    },
    iconContainer: {
      base: 'bg-white',
      selected: 'bg-primary',
    },
    levelIconColor: {
      base: 'var(--color-primary)',
      selected: 'var(--color-primary)',
      inactive: '#F0F0F0',
      selectedInactive: 'var(--color-primary)',
    },
    iconColor: {
      base: '#969696',
      selected: '#FFFFFF',
    },
  },
};

const PrivacyLevelCardWrapper = ({
  selected,
  value,
  baseKey,
  onClick,
  onHover,
  children,
  className,
  variant = 'default',
  disabled,
  disabledTooltip,
}: PropsWithChildren<PrivacyLevelCardWrapperProps>) => {
  const translate = useTranslate();
  const isContributor = useSelector(contributorRole);
  const cardRef = useRef<HTMLDivElement>(null);

  const handleClick = useCallback(() => {
    !isContributor && onClick(value);
  }, [isContributor, onClick, value]);

  useFocusedActions({ ref: cardRef, onAction: handleClick });

  const levelIconColor = useCallback(
    (level: PrivacyLevelsEnum) => {
      if (selected) {
        if (value >= level) return styles[variant].levelIconColor.selected;
        return styles[variant].levelIconColor.selectedInactive;
      }
      if (value >= level) return styles[variant].levelIconColor.base;
      return styles[variant].levelIconColor.inactive;
    },
    [selected, value, variant],
  );

  return (
    <TooltipWrapper content={translate('tooltips.contributorRole')} disabled={!isContributor}>
      <div
        ref={cardRef}
        className={classNames(
          'rounded-30 p-3 cursor-pointer break-words shrink-0',
          styles[variant].card.base,
          { [styles[variant].card.selected]: selected },
          className,
        )}
        onClick={() => !disabled && handleClick()}
        onMouseEnter={onHover}
        tabIndex={0}
      >
        <DisabledOverlay
          containerClassName='flex flex-col items-center'
          enabled={!!disabled}
          tooltipProps={{ content: disabledTooltip }}
        >
          <RadioButtonThick selected={selected} className='absolute top-18px right-18px' />
          <div
            className={classNames('relative h-60px w-60px rounded-full p-1 flex justify-center items-center my-4', {
              [styles[variant].iconContainer.base]: !selected,
              [styles[variant].iconContainer.selected]: selected,
            })}
          >
            <div className='grid grid-cols-2 gap-0.5'>
              <LevelIcon color={levelIconColor(PrivacyLevelsEnum.completePrivacy)} />
              <LevelIcon className='rotate-90' color={levelIconColor(PrivacyLevelsEnum.defaultPrivacy)} />
              <LevelIcon className='-rotate-90' color={levelIconColor(PrivacyLevelsEnum.GDPRMode)} />
              <LevelIcon className='rotate-180' color={levelIconColor(PrivacyLevelsEnum.basicPrivacy)} />
            </div>
            <PrivacyLockIcon
              color={selected ? styles[variant].iconColor.selected : styles[variant].iconColor.base}
              className='absolute'
              level={value}
            />
          </div>
          <Paragraph size={paragraphSizes.large} colorClassName='text-text-dark' weight={fontWeights.semibold}>
            {translate(`${baseKey}.title`)}
          </Paragraph>
          <Paragraph
            size={variant === 'wix-mini' ? paragraphSizes.normal : paragraphSizes.tiny}
            colorClassName='text-text-primary'
            weight={fontWeights.medium}
          >
            {translate(`${baseKey}.subtitle`)}
          </Paragraph>
        </DisabledOverlay>
        {children}
      </div>
    </TooltipWrapper>
  );
};

export default PrivacyLevelCardWrapper;
